import React from 'react'
import { Pagination as BootstrapPagination } from 'react-bootstrap'
import * as S from './styles'

export interface PaginationInfo {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrevious: boolean
}

export interface PaginationProps {
  pagination: PaginationInfo
  onPageChange: (page: number) => void
  onLimitChange?: (limit: number) => void
  showLimitSelector?: boolean
  limitOptions?: number[]
}

export const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  onLimitChange,
  showLimitSelector = true,
  limitOptions = [10, 25, 50, 100]
}) => {
  const { page, limit, total, totalPages, hasNext, hasPrevious } = pagination

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage)
    }
  }

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(event.target.value)
    if (onLimitChange) {
      onLimitChange(newLimit)
    }
  }

  const renderPaginationItems = () => {
    const items = []
    const maxVisiblePages = 5

    // Calculate start and end pages
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    // Adjust startPage if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    // First page
    if (startPage > 1) {
      const isFirstPageActive = page === 1
      items.push(
        <BootstrapPagination.Item
          key={1}
          active={isFirstPageActive}
          onClick={() => handlePageChange(1)}
          style={
            isFirstPageActive
              ? {
                  backgroundColor: '#007bff',
                  borderColor: '#007bff',
                  color: 'white',
                  fontWeight: 'bold'
                }
              : undefined
          }
        >
          1
        </BootstrapPagination.Item>
      )

      if (startPage > 2) {
        items.push(<BootstrapPagination.Ellipsis key="ellipsis-start" />)
      }
    }

    // Visible pages
    for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
      const isActive = page === pageNum
      items.push(
        <BootstrapPagination.Item
          key={pageNum}
          active={isActive}
          onClick={() => handlePageChange(pageNum)}
          style={
            isActive
              ? {
                  backgroundColor: '#007bff',
                  borderColor: '#007bff',
                  color: 'white',
                  fontWeight: 'bold'
                }
              : undefined
          }
        >
          {pageNum}
        </BootstrapPagination.Item>
      )
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<BootstrapPagination.Ellipsis key="ellipsis-end" />)
      }

      const isLastPageActive = page === totalPages
      items.push(
        <BootstrapPagination.Item
          key={totalPages}
          active={isLastPageActive}
          onClick={() => handlePageChange(totalPages)}
          style={
            isLastPageActive
              ? {
                  backgroundColor: '#007bff',
                  borderColor: '#007bff',
                  color: 'white',
                  fontWeight: 'bold'
                }
              : undefined
          }
        >
          {totalPages}
        </BootstrapPagination.Item>
      )
    }

    return items
  }

  const startRecord = (page - 1) * limit + 1
  const endRecord = Math.min(page * limit, total)

  if (total === 0) {
    return (
      <S.PaginationContainer>
        <S.PaginationInfo>Nenhum resultado encontrado</S.PaginationInfo>
      </S.PaginationContainer>
    )
  }

  return (
    <S.PaginationContainer>
      <S.PaginationInfo>
        Mostrando {startRecord} a {endRecord} de {total} registros
        <S.PageIndicator>
          Página {page} de {totalPages}
        </S.PageIndicator>
      </S.PaginationInfo>

      <S.PaginationControls>
        {showLimitSelector && onLimitChange && (
          <S.LimitSelector>
            <label htmlFor="limit-select">Itens por página:</label>
            <select
              id="limit-select"
              value={limit}
              onChange={handleLimitChange}
            >
              {limitOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </S.LimitSelector>
        )}

        <BootstrapPagination>
          <BootstrapPagination.First
            disabled={!hasPrevious}
            onClick={() => handlePageChange(1)}
          />
          <BootstrapPagination.Prev
            disabled={!hasPrevious}
            onClick={() => handlePageChange(page - 1)}
          />

          {renderPaginationItems()}

          <BootstrapPagination.Next
            disabled={!hasNext}
            onClick={() => handlePageChange(page + 1)}
          />
          <BootstrapPagination.Last
            disabled={!hasNext}
            onClick={() => handlePageChange(totalPages)}
          />
        </BootstrapPagination>
      </S.PaginationControls>
    </S.PaginationContainer>
  )
}
